import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { TableValue } from './ui/table-value';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandItem
} from './ui/command';
import { AlertCircle, ChevronsUpDown, Check, ChevronDown, ChevronUp, Minus, TrendingDown } from 'lucide-react';
import {
  fetchEmployeeDailyWorkload,
  fetchEmployeeEfficiency,
  fetchEmployeeKpi,
  fetchEmployeeCallsDynamics,
  fetchEmployeeQualityComparison,
  fetchEmployeeQualityTrend,
  fetchEmployeeQualityForms,
  fetchEmployeeHourlyWorkload,
  fetchEmployeeHourlyWorkloadByDay,
  fetchEmployeeForbiddenPhrases,
  fetchEmployeeHourlyWaitWorkload
} from '../api';
import { CALLS_METRIC_OPTIONS } from '../config/endoTaxonomy';
import {
  buildEfficiencyColumns,
  EMPLOYEE_COLUMN_CLASS,
  readCallCount,
  readComplaintCount,
  readEfficiencyRows,
  readFiniteNumber,
  readFunnelStages,
  readLowSampleThreshold,
  readStageCount,
  resolveComplaintStyle
} from '../utils/employeeEfficiency';
import {
  ariaSortFor,
  resolveEfficiencySort,
  sortEfficiencyRows,
  toggleEfficiencySort
} from '../utils/employeeEfficiencySort';
import {
  describeHighlight,
  describeInsufficientData,
  EFFICIENCY_HIGHLIGHT_STYLES,
  INSUFFICIENT_DATA_LABEL,
  rankEfficiencyHighlights
} from '../utils/employeeEfficiencyHighlight';
import { formatKpiMetric } from '../utils/kpiMetric';
import { persistEfficiencySort, readEfficiencySort } from '../config/employeeEfficiencySortState';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import useEmployees from '../hooks/useEmployees';
import useNodeCatalog from '../hooks/useNodeCatalog';
import useTimeZone from '../hooks/useTimeZone';
import useLatestRequest from '../hooks/useLatestRequest';
import { formatDayMonth } from '../utils/dateTime';
import { TrendingUp, Users, Clock, Activity } from 'lucide-react';
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
  BarChart as ReBarChart,
  Bar,
  ReferenceLine
} from 'recharts';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useNodeChartItems from './nodes/useNodeChartItems';
import SectionGrid from './layout/SectionGrid';
import PlotBox from './layout/PlotBox';
import { buildTabDefaults } from '../config/sectionLayouts';
import NodeFiltersPanel from './nodes/NodeFiltersPanel';
import { COMPACT_CATEGORY_TICK, resolveAxisWidth } from './nodes/nodeChartTheme';
import { MAX_NODE_FILTERS, buildNodeFilterEntries, selectNodeCatalogFilters } from './nodes/nodeFilterUtils';
import {
  buildEmployeeFilterParams,
  buildEmployeeJournalScope,
  buildEmployeeNodeScope,
  hasEmployeeScope,
  withQualityForm
} from './employeeFilterParams';
import QualityComparisonChart, { REFERENCE_LINE_PROPS, ReferenceSwatch } from './employees/QualityComparisonChart';
import QualityFormSelect from './employees/QualityFormSelect';
import InfoHint from './InfoHint';
import { KpiStrip, KpiTile } from './KpiStrip';
import { buildTrendReference, planBenchmarkRequests, readQualityComparison } from './employees/qualityBenchmark';
import { useIsMobile } from '../hooks/useMediaQuery';
import { trackEvent } from '../lib/usageEvents';

const chartPalette = ['#2563eb', '#db2777', '#16a34a', '#f97316', '#7c3aed', '#0ea5e9', '#facc15', '#14b8a6', '#ef4444', '#64748b'];

const formatDateLabel = (value, timeZone) => {
  if (!value) {
    return '—';
  }

  try {
    if (value instanceof Date || typeof value === 'string') {
      const formatted = formatDayMonth(value, timeZone);
      return formatted === '—' ? String(value) : formatted;
    }
    return String(value);
  } catch {
    return value;
  }
};

const prepareMultiSeriesData = (points, timeZone) => {
  if (!points?.length) {
    return { data: [], series: [] };
  }

  const seriesSet = new Set();
  points.forEach((point) => {
    Object.entries(point.values ?? {}).forEach(([name, value]) => {
      if (value !== null && value !== undefined) {
        seriesSet.add(name || '—');
      }
    });
  });

  const series = Array.from(seriesSet).sort((a, b) =>
    a.localeCompare(b, 'ru', { sensitivity: 'base' })
  );

  const data = points.map((point) => {
    const row = { label: formatDateLabel(point.day?.toString?.() ?? point.day, timeZone) };
    series.forEach((key) => {
      const value = point.values?.[key];
      row[key] = typeof value === 'number' ? Number(value) : null;
    });
    return row;
  });

  return { data, series };
};

const ChartEmptyState = ({ message = 'Нет данных для отображения' }) => (
  <div className="flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
    {message}
  </div>
);

const callsAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';
const callsTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} звонков` : '—';
const qualityAxisFormatter = (value) =>
  typeof value === 'number' ? value.toFixed(1) : value ?? '—';
const qualityTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toFixed(1)} балл` : '—';
const minutesAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : value ?? '—';
const minutesTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} мин` : '—';

const createLabelRenderer = (color, formatter) => {
  const ChartValueLabel = (props) => {
    const { x, y, value, width } = props;
    if (value === null || value === undefined) {
      return null;
    }

    const baseX = typeof x === 'number' ? x : 0;
    const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;

    return (
      <text
        x={centerX}
        y={(y ?? 0) - 8}
        textAnchor="middle"
        fill={color}
        fontSize="11"
        fontWeight="600"
      >
        {formatter ? formatter(value) : value}
      </text>
    );
  };

  // Имя нужно не только линтеру: recharts клонирует этот элемент, и в React DevTools
  // безымянная стрелка выглядит как anonymous среди десятка таких же.
  ChartValueLabel.displayName = 'ChartValueLabel';

  return ChartValueLabel;
};

const tooltipWrapperStyle = { zIndex: 30 };

/** Высота графиков динамики и нагрузки вне сетки и нижняя граница в ней — бывшие `h-64`. */
const TREND_PLOT_HEIGHT = 256;

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md max-sm:max-w-[240px] max-sm:text-xs"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
          <span>
            {item.name}: {formatter ? formatter(item.value, item.name) : item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * На телефоне (≈250px ширины графика):
 * - подписи значений у точек не рисуются — при десятках точек они сливаются в кашу,
 *   значение по-прежнему видно в тултипе;
 * - легенда уходит под график HTML-списком: recharts-легенда с одной записью на сотрудника
 *   переносилась на четыре-пять строк внутри фиксированной высоты и сжимала сам график
 *   (тот же приём, что в `NodeTimeseriesChart`).
 *
 * `reference` — горизонталь ориентира `{ value, caption }` (среднее по всем сотрудникам на
 * «Динамике качества работы»). Подпись линии — под графиком: в самой области её перекрыли бы
 * подписи значений у точек. `extendDomain` обязателен: в recharts 3 умолчание `discard`, и
 * линия молча пропадает, если среднее лежит вне диапазона линий — ровно при фильтре на
 * одного сотрудника.
 */
const MultiLineChart = ({ data, series, axisFormatter, tooltipFormatter, labelFormatter, reference }) => {
  const isMobile = useIsMobile();
  const hasReference = Number.isFinite(reference?.value);

  if (!data?.length || !series?.length) {
    return <ChartEmptyState />;
  }

  return (
    <>
      <PlotBox height={TREND_PLOT_HEIGHT}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
          <ReLineChart data={data} margin={{ top: 24, right: isMobile ? 8 : 24, left: isMobile ? 0 : 8, bottom: 12 }}>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis
              width={isMobile ? 40 : 60}
              tick={{ fontSize: 11 }}
              tickFormatter={axisFormatter}
              allowDecimals
            />
            <RechartsTooltip
              wrapperStyle={tooltipWrapperStyle}
              content={<ChartTooltip formatter={tooltipFormatter || axisFormatter} />}
            />
            {!isMobile && <Legend wrapperStyle={{ fontSize: '12px' }} />}
            {series.map((key, index) => {
              const color = chartPalette[index % chartPalette.length];
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  connectNulls
                >
                  {!isMobile && (
                    <LabelList
                      dataKey={key}
                      content={createLabelRenderer(color, labelFormatter || axisFormatter)}
                    />
                  )}
                </Line>
              );
            })}
            {hasReference && (
              <ReferenceLine y={reference.value} ifOverflow="extendDomain" {...REFERENCE_LINE_PROPS} />
            )}
          </ReLineChart>
        </ResponsiveContainer>
      </PlotBox>
      {hasReference && reference.caption && (
        <p className="mt-2 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" data-quality-average>
          <ReferenceSwatch />
          <span className="break-words">{reference.caption}</span>
        </p>
      )}
      {isMobile && (
        <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {series.map((key, index) => (
            <li key={key} className="flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: chartPalette[index % chartPalette.length] }}
              />
              <span className="break-words">{key}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const SingleBarChart = ({
  data,
  axisFormatter,
  tooltipFormatter,
  labelFormatter,
  seriesLabel = 'Значение'
}) => {
  const isMobile = useIsMobile();

  if (!data?.length) {
    return <ChartEmptyState />;
  }

  const color = '#f59e0b';

  // На телефоне без подписей над столбцами: 24 часовых столбца в ~200px — это по 8px
  // на столбец, подписи наезжали друг на друга. Значение остаётся в тултипе.
  return (
    <PlotBox height={TREND_PLOT_HEIGHT}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
        <ReBarChart data={data} margin={{ top: 24, right: isMobile ? 8 : 24, left: isMobile ? 0 : 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis width={isMobile ? 40 : 60} tick={{ fontSize: 11 }} tickFormatter={axisFormatter} />
          <RechartsTooltip
            wrapperStyle={tooltipWrapperStyle}
            content={<ChartTooltip formatter={tooltipFormatter || axisFormatter} />}
          />
          <Bar
            dataKey="value"
            name={seriesLabel}
            fill={color}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          >
            {!isMobile && (
              <LabelList
                dataKey="value"
                content={createLabelRenderer(color, labelFormatter || axisFormatter)}
              />
            )}
          </Bar>
        </ReBarChart>
      </ResponsiveContainer>
    </PlotBox>
  );
};

const ForbiddenPhraseChart = ({ rows, onOpenCallJournal }) => {
  const isMobile = useIsMobile();
  const [chartWidth, setChartWidth] = useState(0);

  if (!rows?.length) {
    return <ChartEmptyState message="Запрещённые фразы не найдены в клиентских звонках" />;
  }

  const height = Math.max(220, rows.length * 42 + 44);
  // Фиксированные 210px оси плюс 96px справа шире всего графика на телефоне — столбцам
  // не оставалось места. Там колонку считает общий `resolveAxisWidth`, а справа хватает
  // места под число: подпись здесь и так без доли.
  const axisWidth = isMobile ? resolveAxisWidth(rows, { compact: true, containerWidth: chartWidth }) : 210;

  return (
    <div className="w-full min-w-0" style={{ height }}>
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        minHeight={160}
        onResize={(width) => setChartWidth(width)}
      >
        <ReBarChart
          data={rows}
          layout="vertical"
          margin={{ top: 8, right: isMobile ? 32 : 96, left: isMobile ? 0 : 8, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="label"
            width={axisWidth}
            tick={isMobile ? COMPACT_CATEGORY_TICK : { fontSize: 11 }}
            interval={0}
          />
          <RechartsTooltip
            wrapperStyle={tooltipWrapperStyle}
            content={<ChartTooltip formatter={(value) => `${value} звонков`} />}
          />
          <Bar
            dataKey="value"
            name="Звонки"
            fill="#ef4444"
            radius={[0, 4, 4, 0]}
            maxBarSize={28}
            cursor="pointer"
            onClick={(entry) => {
              trackEvent('employees.forbidden_phrases.open_journal');
              const item = entry?.payload ?? entry;
              if (item?.criterion) {
                onOpenCallJournal?.({ criterion: item.criterion, criterion_max_score: 0.5 });
                return;
              }
              onOpenCallJournal?.({ search: item?.search });
            }}
          >
            <LabelList
              dataKey="value"
              content={({ x, y, width, height: barHeight, value }) => {
                if (value === null || value === undefined) return null;
                return (
                  <text
                    x={(x ?? 0) + (width ?? 0) + 6}
                    y={(y ?? 0) + (barHeight ?? 0) / 2}
                    dominantBaseline="middle"
                    fill="hsl(var(--foreground))"
                    fontSize="11"
                    fontWeight="600"
                  >
                    {value}
                  </text>
                );
              }}
            />
          </Bar>
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
};

const EmployeeAnalytics = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const { timeZone } = useTimeZone();
  const panels = usePanelSettings();
  const { employees, error: employeesError } = useEmployees();
  const [selectedEmployees, setSelectedEmployees] = useState(['all']);
  const [selectedDepartments, setSelectedDepartments] = useState(['all']);
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);

  // ── Фильтры по результатам анализа ──────────────────────────────────────────
  // Действуют на весь раздел, а не только на графики нод внизу, поэтому панель живёт
  // в шапке, а секция нод получает готовую строку. Бизнес-тип звонка («Тип обращения»)
  // выбирается здесь — прежний фильтр «Типы звонков» читал колонку, которую движок нод
  // не пишет. Каталог — из кэша модуля, второго запроса за ним не будет.
  const nodeCatalog = useNodeCatalog();
  const { catalog } = nodeCatalog;
  const nodeCatalogFilters = useMemo(() => selectNodeCatalogFilters(catalog), [catalog]);
  const [nodeFilterValues, setNodeFilterValues] = useState({});
  const nodeFilterEntries = useMemo(
    () => buildNodeFilterEntries(nodeFilterValues, nodeCatalogFilters),
    [nodeFilterValues, nodeCatalogFilters]
  );
  const nodeFilter = nodeFilterEntries.length > 0
    ? nodeFilterEntries.slice(0, MAX_NODE_FILTERS).join(';')
    : undefined;

  const handleNodeFilterChange = useCallback((id, value) => {
    setNodeFilterValues((current) => ({ ...current, [id]: value }));
  }, []);

  // Объект среза обязан быть стабильным: графики нод перезапрашивают данные при смене
  // identity параметров, и новый объект на каждом рендере гонял бы их по кругу.
  const nodeScope = useMemo(
    () => buildEmployeeNodeScope({ employees: selectedEmployees, departments: selectedDepartments }),
    [selectedEmployees, selectedDepartments]
  );

  // Переход в журнал уносит срез раздела: сотрудников, отделы и фильтры анализа. Направление
  // не уносит — оно в верхней панели, общей с журналом. Ключи самого перехода (поиск,
  // критерий, условие кликнутого графика) идут поверх.
  const openJournal = useCallback(
    (payload) => onOpenCallJournal?.({
      ...buildEmployeeJournalScope({ employees: selectedEmployees, departments: selectedDepartments, nodeFilter }),
      ...payload
    }),
    [onOpenCallJournal, selectedEmployees, selectedDepartments, nodeFilter]
  );

  const [kpi, setKpi] = useState(null);
  const [efficiency, setEfficiency] = useState([]);
  // Этапы воронки приходят с сервера вместе со строками: их состав задаёт активный пакет
  // узлов, поэтому другая воронка меняет колонки таблицы без правок кода.
  const [funnelStages, setFunnelStages] = useState([]);
  // Порог «Недостаточно данных» — из того же ответа: число в подсказках не дублирует сервер.
  const [lowSampleThreshold, setLowSampleThreshold] = useState(null);
  // Сортировка таблицы переживает уход из раздела: компонент размонтируется при каждом
  // переключении, поэтому выбор лежит в localStorage, а не только в состоянии.
  const [efficiencySort, setEfficiencySort] = useState(readEfficiencySort);
  const [qualityForms, setQualityForms] = useState([]);
  const [dailyWorkload, setDailyWorkload] = useState([]);
  const [hourlyWorkload, setHourlyWorkload] = useState([]);
  const [hourlyByDay, setHourlyByDay] = useState([]);
  const [hourlyWaitWorkload, setHourlyWaitWorkload] = useState([]);
  const [qualityTrend, setQualityTrend] = useState([]);
  const [callsDynamics, setCallsDynamics] = useState([]);
  const [forbiddenPhrases, setForbiddenPhrases] = useState([]);
  const [discoveredEmployees, setDiscoveredEmployees] = useState([]);
  const [qualityTrendForm, setQualityTrendForm] = useState('all');
  const [callsMetric, setCallsMetric] = useState('calls');
  const [, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const requests = useLatestRequest();
  const REFRESH_INTERVAL_MS = GRAPH_REFRESH_INTERVAL_MS;

  const loadData = useCallback(async (showSpinner = true) => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    const request = requests.begin({ background: showSpinner === false });
    if (!request) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    setError(null);

    const commonParams = buildEmployeeFilterParams({
      dateRange,
      employees: selectedEmployees,
      departments: selectedDepartments,
      callType,
      nodeFilter
    });

    const qualityTrendParams = withQualityForm(commonParams, qualityTrendForm);

    const callsDynamicsParams = {
      ...commonParams,
      ...(callsMetric !== 'calls' ? { metric: callsMetric } : {})
    };

    try {
      const [
        kpiData,
        efficiencyData,
        workloadData,
        hourlyData,
        hoursByDayData,
        hourlyWaitData,
        callsData,
        qualityData,
        forbiddenPhrasesData
      ] = await Promise.all([
        fetchEmployeeKpi(commonParams),
        fetchEmployeeEfficiency(commonParams),
        fetchEmployeeDailyWorkload(commonParams),
        fetchEmployeeHourlyWorkload(commonParams),
        fetchEmployeeHourlyWorkloadByDay(commonParams),
        fetchEmployeeHourlyWaitWorkload(commonParams),
        fetchEmployeeCallsDynamics(callsDynamicsParams),
        fetchEmployeeQualityTrend(qualityTrendParams),
        fetchEmployeeForbiddenPhrases(commonParams)
      ]);

      if (!request.isCurrent()) {
        return;
      }

      setKpi(kpiData);
      const efficiencyRowsData = readEfficiencyRows(efficiencyData);
      setEfficiency(efficiencyRowsData);
      setFunnelStages(readFunnelStages(efficiencyData));
      setLowSampleThreshold(readLowSampleThreshold(efficiencyData));
      setDiscoveredEmployees((prev) => mergeEmployeeOptions(prev, efficiencyRowsData));
      setDailyWorkload(workloadData ?? []);
      setHourlyWorkload(hourlyData ?? []);
      setHourlyByDay(hoursByDayData ?? []);
      setHourlyWaitWorkload(hourlyWaitData ?? []);
      setCallsDynamics(callsData ?? []);
      setQualityTrend(qualityData ?? []);
      setForbiddenPhrases(Array.isArray(forbiddenPhrasesData) ? forbiddenPhrasesData : []);
    } catch (err) {
      console.error(err);
      if (request.isCurrent()) {
        setError('Не удалось загрузить аналитику по сотрудникам.');
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (showSpinner && isCurrent) {
        setIsLoading(false);
      }
    }
  }, [dateRange, callType, nodeFilter, selectedDepartments, selectedEmployees, qualityTrendForm, callsMetric, requests]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Средний ориентир качества ─────────────────────────────────────────────
  // Свой загрузчик, а не ещё один запрос в общем Promise.all: план зависит от видимости
  // панелей, и смена видимости на холодном кэше настроек иначе перезапрашивала бы все
  // графики раздела. Скрытые «Сравнение» и «Динамика» ориентир не запрашивают вовсе,
  // при одинаковых формах обоим графикам хватает одного запроса.
  const [qualityComparisonForm, setQualityComparisonForm] = useState('all');
  const [qualityComparison, setQualityComparison] = useState(() => readQualityComparison(null));
  const [qualityTrendBenchmark, setQualityTrendBenchmark] = useState(() => readQualityComparison(null));
  const [qualityComparisonError, setQualityComparisonError] = useState(false);
  const benchmarkRequests = useLatestRequest();
  const isQualityComparisonVisible = panels.isVisible(PANEL_IDS['ui:employees.quality-comparison']);
  const isQualityTrendVisible = panels.isVisible(PANEL_IDS['ui:employees.quality-trend']);
  const isEmployeeScopeFiltered = hasEmployeeScope({ employees: selectedEmployees, departments: selectedDepartments });

  const benchmarkPlan = useMemo(
    () => planBenchmarkRequests({
      comparisonVisible: isQualityComparisonVisible,
      comparisonForm: qualityComparisonForm,
      trendVisible: isQualityTrendVisible,
      trendForm: qualityTrendForm
    }),
    [isQualityComparisonVisible, qualityComparisonForm, isQualityTrendVisible, qualityTrendForm]
  );

  const loadBenchmarks = useCallback(async (showSpinner = true) => {
    if (!dateRange?.from || !dateRange?.to || benchmarkPlan.length === 0) {
      return;
    }

    const request = benchmarkRequests.begin({ background: showSpinner === false });
    if (!request) {
      return;
    }

    const commonParams = buildEmployeeFilterParams({
      dateRange,
      employees: selectedEmployees,
      departments: selectedDepartments,
      callType,
      nodeFilter
    });

    try {
      const responses = await Promise.all(
        benchmarkPlan.map(({ form }) => fetchEmployeeQualityComparison(withQualityForm(commonParams, form)))
      );

      if (!request.isCurrent()) {
        return;
      }

      benchmarkPlan.forEach(({ targets }, index) => {
        const comparison = readQualityComparison(responses[index]);
        if (targets.includes('comparison')) {
          setQualityComparison(comparison);
        }
        if (targets.includes('trend')) {
          setQualityTrendBenchmark(comparison);
        }
      });
      setQualityComparisonError(false);
    } catch (err) {
      console.error(err);
      if (request.isCurrent()) {
        setQualityComparisonError(true);
      }
    } finally {
      request.end();
    }
  }, [dateRange, callType, nodeFilter, selectedDepartments, selectedEmployees, benchmarkPlan, benchmarkRequests]);

  useEffect(() => {
    if (benchmarkPlan.length === 0) {
      return undefined;
    }

    loadBenchmarks();
    const intervalId = setInterval(() => loadBenchmarks(false), GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadBenchmarks, benchmarkPlan.length]);

  const qualityTrendReference = useMemo(
    () => buildTrendReference(qualityTrendBenchmark, { filtered: isEmployeeScopeFiltered }),
    [qualityTrendBenchmark, isEmployeeScopeFiltered]
  );

  // Формы чек-листа не зависят от периода и фильтров — тянем один раз за монтирование.
  useEffect(() => {
    let cancelled = false;

    fetchEmployeeQualityForms()
      .then((forms) => {
        if (!cancelled) {
          setQualityForms(Array.isArray(forms) ? forms : []);
        }
      })
      .catch(() => {
        // Пустой список — не ошибка экрана: селектор просто останется на legacy-перечне.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadData(false);
    }, REFRESH_INTERVAL_MS);

    const handleClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (target.closest('[data-employee-dropdown]') || target.closest('[data-department-dropdown]')) {
        return;
      }

      setIsEmployeeDropdownOpen(false);
      setIsDepartmentDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [loadData]);

  const employeeOptions = useMemo(() => {
    const options = employees.map((emp) => ({
      id: emp.id?.toString() ?? emp.name,
      name: emp.name ?? emp.fullName ?? 'Без имени',
      department: emp.department
    }));

    return mergeEmployeeOptions(options, discoveredEmployees);
  }, [employees, discoveredEmployees]);

  const departmentOptions = useMemo(
    () => [...new Set(employeeOptions.map((employee) => employee.department).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' })),
    [employeeOptions]
  );

  const filteredEmployeeOptions = useMemo(() => {
    if (selectedDepartments.includes('all')) {
      return employeeOptions;
    }

    return employeeOptions.filter(
      (employee) => employee.department && selectedDepartments.includes(employee.department)
    );
  }, [employeeOptions, selectedDepartments]);

  useEffect(() => {
    if (selectedEmployees.includes('all')) {
      return;
    }

    const allowedIds = new Set(filteredEmployeeOptions.map((employee) => employee.id));
    const nextSelected = selectedEmployees.filter((id) => allowedIds.has(id));

    if (nextSelected.length === selectedEmployees.length) {
      return;
    }

    setSelectedEmployees(nextSelected.length > 0 ? nextSelected : ['all']);
  }, [filteredEmployeeOptions, selectedEmployees]);

  // Состав колонок держит employeeEfficiency.js: заголовки и colSpan обязаны считаться по одному
  // списку, иначе они расходятся — см. buildEfficiencyColumns.
  const efficiencyColumns = useMemo(() => buildEfficiencyColumns(funnelStages), [funnelStages]);

  // Сохранённый выбор сверяется с текущими столбцами: этап воронки мог уйти со сменой пакета.
  const activeEfficiencySort = useMemo(
    () => resolveEfficiencySort(efficiencySort, efficiencyColumns),
    [efficiencySort, efficiencyColumns]
  );

  // Строки с недостаточными данными сортировка держит внизу — см. employeeEfficiencySort.js.
  const efficiencyRows = useMemo(
    () => sortEfficiencyRows(efficiency, activeEfficiencySort, funnelStages),
    [efficiency, activeEfficiencySort, funnelStages]
  );

  // Подсветка — по всем строкам ответа, от порядка строк не зависит.
  const efficiencyHighlights = useMemo(
    () => ({
      conversion: rankEfficiencyHighlights(efficiency, 'conversion'),
      checklist: rankEfficiencyHighlights(efficiency, 'checklist')
    }),
    [efficiency]
  );

  // Запись — в обработчике клика, а не эффектом: умолчание в хранилище не попадает, и
  // таблица без выбора пользователя следует умолчанию кода, даже если оно сменится.
  const handleEfficiencySort = useCallback(
    (key) => {
      const next = toggleEfficiencySort(activeEfficiencySort, key, efficiencyColumns);
      setEfficiencySort(next);
      persistEfficiencySort(next);
    },
    [activeEfficiencySort, efficiencyColumns]
  );

  const qualityChartData = useMemo(
    () => prepareMultiSeriesData(qualityTrend, timeZone),
    [qualityTrend, timeZone]
  );

  const callsChartData = useMemo(
    () => prepareMultiSeriesData(callsDynamics, timeZone),
    [callsDynamics, timeZone]
  );

  const weekdayChartData = useMemo(
    () =>
      (dailyWorkload ?? []).map((point) => ({
        label: point.day,
        value: point.calls
      })),
    [dailyWorkload]
  );

  const hourlyChartData = useMemo(() => {
    const totals = Array(24).fill(0);

    (hourlyWorkload ?? []).forEach((point) => {
      const hour = Number(point.hour);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return;
      }
      const calls = typeof point.calls === 'number' ? point.calls : Number(point.calls) || 0;
      totals[hour] += calls;
    });

    const activeHours = totals
      .map((value, hour) => ({ hour, value }))
      .filter((item) => item.value > 0);

    if (activeHours.length === 0) {
      return [];
    }

    return activeHours.map(({ hour, value }) => ({
      label: `${String(hour).padStart(2, '0')}:00`,
      value
    }));
  }, [hourlyWorkload]);

  const durationChartData = useMemo(() => {
    const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const totals = new Map();

    (hourlyByDay ?? []).forEach((point) => {
      const label = typeof point.day === 'string' ? point.day : '';
      const rawMinutes =
        typeof point.totalMinutes === 'number'
          ? point.totalMinutes
          : Number(point.totalMinutes) || 0;
      const valueMinutes = Math.round(rawMinutes * 10) / 10; // keep one decimal to avoid zeroing short calls

      if (!label) {
        return;
      }

      const existing = totals.get(label) ?? 0;
      totals.set(label, existing + valueMinutes);
    });

    return weekdays.map((label) => ({
      label,
      value: totals.get(label) ?? 0
    }));
  }, [hourlyByDay]);

  const waitByHourChartData = useMemo(() => {
    const totals = Array(24).fill(0);

    (hourlyWaitWorkload ?? []).forEach((point) => {
      const hour = Number(point.hour);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return;
      }

      const minutes =
        typeof point.waitMinutes === 'number'
          ? point.waitMinutes
          : Number(point.waitMinutes) || 0;
      totals[hour] += Math.round(minutes * 10) / 10;
    });

    const activeHours = totals
      .map((value, hour) => ({ hour, value }))
      .filter((item) => item.value > 0);

    if (activeHours.length === 0) {
      return [];
    }

    return activeHours.map(({ hour, value }) => ({
      label: `${String(hour).padStart(2, '0')}:00`,
      value
    }));
  }, [hourlyWaitWorkload]);

  // Счёт, фильтры и сортировка приехали с сервера — здесь остаётся только переименовать поле
  // под ключ, которым столбики читает график.
  const forbiddenPhraseChartData = useMemo(
    () => (forbiddenPhrases ?? []).map((row) => ({ ...row, value: row.calls })),
    [forbiddenPhrases]
  );

  const kpiCards = [
    { panelId: PANEL_IDS['ui:employees.kpi-avg-calls'], value: formatKpiMetric(kpi?.avgCallsPerEmployee), icon: PhoneIcon },
    { panelId: PANEL_IDS['ui:employees.kpi-avg-duration'], value: formatKpiMetric(kpi?.avgCallDuration), icon: Clock },
    { panelId: PANEL_IDS['ui:employees.kpi-avg-talk'], value: formatKpiMetric(kpi?.avgTalkDuration), icon: Clock },
    { panelId: PANEL_IDS['ui:employees.kpi-avg-wait'], value: formatKpiMetric(kpi?.avgWaitDuration), icon: Clock },
    { panelId: PANEL_IDS['ui:employees.kpi-avg-checklist'], value: formatKpiMetric(kpi?.avgQualityScore), icon: TrendingUp },
    { panelId: PANEL_IDS['ui:employees.kpi-workload'], value: employees.length, icon: Users }
  ].filter((card) => panels.isVisible(card.panelId));

  // Чек-листы качества стоят здесь, а не в общей статистике: тепловая карта строится в разрезе
  // операторов, то есть отвечает на вопрос этой вкладки. Срез у них тот же, что у всего раздела:
  // направление, фильтры анализа из шапки, выбранные сотрудники и отделы.
  const nodeItems = useNodeChartItems({
    tab: 'employees',
    nodeCatalog,
    dateRange,
    callType,
    onOpenCallJournal: openJournal,
    filtersControlled: true,
    nodeFilter,
    scope: nodeScope
  });

  // Шапка вместе с фильтрами по результатам анализа — один элемент сетки: фильтры сужают весь
  // раздел, а не только графики нод, и разносить их с шапкой по разным углам вкладки незачем.
  const headerElement = (
    <div className="flex flex-col gap-6">
      <Card data-panel-id={PANEL_IDS['ui:employees.header']}>
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle>{panels.title(PANEL_IDS['ui:employees.header'])}</CardTitle>
            <InfoHint text={panels.description(PANEL_IDS['ui:employees.header'])} label="О разделе" trackId="employees.header.hint" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="sm:flex-1" data-department-dropdown>
            <p className="mb-1 text-sm text-muted-foreground">Отдел</p>
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                data-ev="employees.filters.department_toggle"
                className="w-full justify-between"
                onClick={() => setIsDepartmentDropdownOpen((prev) => !prev)}
              >
                <span className="truncate">
                  {selectedDepartments.includes('all')
                    ? 'Все отделы'
                    : selectedDepartments.join(', ')}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {isDepartmentDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full max-w-[280px] rounded-md border bg-white p-0 shadow-md">
                  <Command loop autoFocus={true}>
                    <CommandInput placeholder="Поиск отдела..." />
                    <CommandList>
                      <CommandEmpty>Не найдено</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          key="all"
                          trackId="employees.filters.department_all"
                          onSelect={() => {
                            setSelectedDepartments(['all']);
                            setSelectedEmployees(['all']);
                            setIsDepartmentDropdownOpen(false);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${selectedDepartments.includes('all') ? 'opacity-100' : 'opacity-0'}`}
                          />
                          Все отделы
                        </CommandItem>
                        {departmentOptions.map((department) => {
                          const isSelected = selectedDepartments.includes(department);
                          return (
                            <CommandItem
                              key={department}
                              trackId="employees.filters.department_select"
                              onSelect={() => {
                                setSelectedDepartments((prev) => {
                                  if (prev.includes('all')) {
                                    return [department];
                                  }
                                  if (prev.includes(department)) {
                                    const next = prev.filter((item) => item !== department);
                                    return next.length === 0 ? ['all'] : next;
                                  }
                                  return [...prev, department];
                                });
                                setSelectedEmployees(['all']);
                              }}
                              className="flex items-center gap-2"
                            >
                              <Check className={`h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                              {department}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedDepartments.includes('all') ? (
                <Badge variant="outline">Все отделы</Badge>
              ) : (
                selectedDepartments.map((department) => (
                  <Badge key={department} variant="outline">
                    {department}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="sm:flex-1">
            <p className="mb-1 text-sm text-muted-foreground">Сотрудники</p>
            <div className="relative" data-employee-dropdown>
              <Button
                type="button"
                variant="outline"
                data-ev="employees.filters.employee_toggle"
                className="w-full justify-between"
                onClick={() => setIsEmployeeDropdownOpen((prev) => !prev)}
              >
                <span className="truncate">
                  {selectedEmployees.includes('all')
                    ? 'Все сотрудники'
                    : selectedEmployees
                        .map((id) => filteredEmployeeOptions.find((e) => e.id === id)?.name ?? employeeOptions.find((e) => e.id === id)?.name ?? id)
                        .join(', ')}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {isEmployeeDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full max-w-[360px] rounded-md border bg-white p-0 shadow-md">
                  <Command loop autoFocus={true}>
                    <CommandInput placeholder="Поиск сотрудника..." />
                    <CommandList>
                      <CommandEmpty>Не найдено</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          key="all"
                          trackId="employees.filters.employee_all"
                          onSelect={() => {
                            setSelectedEmployees(['all']);
                            setIsEmployeeDropdownOpen(false);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${selectedEmployees.includes('all') ? 'opacity-100' : 'opacity-0'}`}
                          />
                          Все сотрудники
                        </CommandItem>
                        {filteredEmployeeOptions.map((emp) => {
                          const isSelected = selectedEmployees.includes('all')
                            ? true
                            : selectedEmployees.includes(emp.id);
                          return (
                            <CommandItem
                              key={emp.id}
                              trackId="employees.filters.employee_select"
                              onSelect={() => {
                                setSelectedEmployees((prev) => {
                                  if (prev.includes('all')) {
                                    return [emp.id];
                                  }
                                  if (prev.includes(emp.id)) {
                                    const next = prev.filter((id) => id !== emp.id);
                                    return next.length === 0 ? ['all'] : next;
                                  }
                                  return [...prev, emp.id];
                                });
                              }}
                              className="flex items-center gap-2"
                            >
                              <Check className={`h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                              {emp.name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedEmployees.includes('all') ? (
                <Badge variant="outline">Все сотрудники</Badge>
              ) : (
                selectedEmployees.map((id) => {
                  const emp = filteredEmployeeOptions.find((e) => e.id === id) ?? employeeOptions.find((e) => e.id === id);
                  return (
                    <Badge key={id} variant="outline">
                      {emp?.name ?? id}
                    </Badge>
                  );
                })
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Фильтры по результатам анализа — часть шапки раздела: они сужают все графики
          ниже, а не только графики нод. Бизнес-тип звонка («Тип обращения») выбирается
          здесь. */}
      {nodeCatalogFilters.length > 0 && (
        <NodeFiltersPanel
          filters={nodeCatalogFilters}
          values={nodeFilterValues}
          onChange={handleNodeFilterChange}
          onReset={() => setNodeFilterValues({})}
          packName={catalog.packName}
          packVersion={catalog.packVersion}
          entryCount={nodeFilterEntries.length}
        />
      )}
    </div>
  );

  // Подписи плиток приходят из реестра панелей: здесь только привязка к метрике. Ключом идёт
  // panelId, а не индекс — после отсева скрытых индекс не стабилен.
  const kpiStripElement = kpiCards.length > 0 && (
        <KpiStrip count={kpiCards.length}>
          {kpiCards.map((card) => (
            <KpiTile
              key={card.panelId}
              panelId={card.panelId}
              title={panels.title(card.panelId)}
              hint={(
                <InfoHint
                  text={panels.description(card.panelId)}
                  label={`О показателе «${panels.title(card.panelId)}»`}
                  trackId="employees.kpi.hint"
                />
              )}
              value={card.value}
              icon={card.icon}
              twoLineTitle
            />
          ))}
        </KpiStrip>
  );

  const efficiencyElement = panels.isVisible(PANEL_IDS['ui:employees.operator-efficiency']) && (
      <Card data-panel-id={PANEL_IDS['ui:employees.operator-efficiency']}>
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle>{panels.title(PANEL_IDS['ui:employees.operator-efficiency'])}</CardTitle>
            {/* Подпись зависит от того, есть ли воронка в пакете, — переименованию не поддаётся. */}
            <InfoHint
              text={funnelStages.length > 0
                ? 'Всего звонков и этапы воронки по каждому сотруднику: звонки, дошедшие до этапа или дальше'
                : 'Связка активности и качества по каждому сотруднику'}
              label="О показателях сотрудников"
              trackId="employees.operator_efficiency.hint"
            />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {efficiencyColumns.map((column) => (
                  <TableHead
                    key={column.key}
                    numeric={column.numeric}
                    className={column.className}
                    title={column.title}
                    aria-sort={ariaSortFor(activeEfficiencySort, column.key)}
                  >
                    <EfficiencySortButton
                      label={column.label}
                      numeric={column.numeric}
                      direction={
                        activeEfficiencySort.key === column.key ? activeEfficiencySort.direction : null
                      }
                      onClick={() => handleEfficiencySort(column.key)}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {efficiencyRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={efficiencyColumns.length} className="text-center text-muted-foreground">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
              {efficiencyRows.map((row) => (
                <TableRow key={row.employeeId ?? row.employeeName}>
                  <TableCell className={`whitespace-nowrap font-medium ${EMPLOYEE_COLUMN_CLASS}`}>{row.employeeName || '—'}</TableCell>
                  <TableCell numeric>{readCallCount(row).toLocaleString('ru-RU')}</TableCell>
                  {funnelStages.map((stage, index) => {
                    const count = readStageCount(row, index);

                    return (
                      <TableCell key={stage.code} numeric className={count === 0 ? 'text-muted-foreground' : undefined}>
                        {count.toLocaleString('ru-RU')}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    data-column="conversion"
                    data-highlight={efficiencyHighlights.conversion.get(row)}
                  >
                    <ConversionCell
                      value={row.conversionRate}
                      highlight={efficiencyHighlights.conversion.get(row)}
                      insufficient={row.lowSample === true}
                      threshold={lowSampleThreshold}
                    />
                  </TableCell>
                  <TableCell
                    data-column="checklist"
                    data-highlight={efficiencyHighlights.checklist.get(row)}
                  >
                    <ChecklistTotalCell
                      value={row.avgFirstOperatorTotal}
                      highlight={efficiencyHighlights.checklist.get(row)}
                      insufficient={row.checklistLowSample === true}
                      threshold={lowSampleThreshold}
                    />
                  </TableCell>
                  <TableCell data-column="complaints" numeric>
                    <ComplaintCount count={readComplaintCount(row)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {efficiencyRows.some((row) => row.lowSample === true || row.checklistLowSample === true) && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              {INSUFFICIENT_DATA_LABEL}: значение не участвует в сравнении лучших и худших.
            </p>
          )}
        </CardContent>
      </Card>
  );

  const qualityComparisonElement = isQualityComparisonVisible && (
        <Card data-panel-id={PANEL_IDS['ui:employees.quality-comparison']}>
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.quality-comparison'])}</CardTitle>
                <InfoHint text={panels.description(PANEL_IDS['ui:employees.quality-comparison'])} label="О графике" trackId="employees.quality_comparison.hint" />
              </div>
            </div>
            <QualityFormSelect
              trackId="employees.quality_comparison.form"
              value={qualityComparisonForm}
              onChange={setQualityComparisonForm}
              forms={qualityForms}
            />
          </CardHeader>
          <CardContent>
            {qualityComparisonError ? (
              <ChartEmptyState message="Не удалось загрузить сравнение качества" />
            ) : (
              <QualityComparisonChart comparison={qualityComparison} filtered={isEmployeeScopeFiltered} />
            )}
          </CardContent>
        </Card>
  );

  const qualityTrendElement = isQualityTrendVisible && (
        <Card data-panel-id={PANEL_IDS['ui:employees.quality-trend']} className="flex flex-col">
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.quality-trend'])}</CardTitle>
                <InfoHint text={panels.description(PANEL_IDS['ui:employees.quality-trend'])} label="О графике" trackId="employees.quality_trend.hint" />
              </div>
            </div>
            <QualityFormSelect trackId="employees.quality_trend.form" value={qualityTrendForm} onChange={setQualityTrendForm} forms={qualityForms} />
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <MultiLineChart
              data={qualityChartData.data}
              series={qualityChartData.series}
              axisFormatter={qualityAxisFormatter}
              labelFormatter={qualityAxisFormatter}
              tooltipFormatter={qualityTooltipFormatter}
              reference={qualityTrendReference}
            />
          </CardContent>
        </Card>
  );

  const callsTrendElement = panels.isVisible(PANEL_IDS['ui:employees.calls-trend']) && (
        <Card data-panel-id={PANEL_IDS['ui:employees.calls-trend']} className="flex flex-col">
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.calls-trend'])}</CardTitle>
                <InfoHint text={panels.description(PANEL_IDS['ui:employees.calls-trend'])} label="О графике" trackId="employees.calls_trend.hint" />
              </div>
            </div>
            <select
              data-ev="employees.calls_trend.metric"
              data-ev-send-value
              value={callsMetric}
              onChange={(event) => setCallsMetric(event.target.value)}
              className="rounded-md border bg-background px-2 py-1 text-sm text-foreground"
            >
              {CALLS_METRIC_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <MultiLineChart
              data={callsChartData.data}
              series={callsChartData.series}
              axisFormatter={callsAxisFormatter}
              labelFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
            />
          </CardContent>
        </Card>
  );

  const weekdayWorkloadElement = panels.isVisible(PANEL_IDS['ui:employees.weekday-workload']) && (
        <Card data-panel-id={PANEL_IDS['ui:employees.weekday-workload']} className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.weekday-workload'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:employees.weekday-workload'])} label="О графике" trackId="employees.weekday_workload.hint" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <SingleBarChart
              data={weekdayChartData}
              axisFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
              labelFormatter={callsAxisFormatter}
              seriesLabel="Звонки"
            />
          </CardContent>
        </Card>
  );

  const hourlyWorkloadElement = panels.isVisible(PANEL_IDS['ui:employees.hourly-workload']) && (
        <Card data-panel-id={PANEL_IDS['ui:employees.hourly-workload']} className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.hourly-workload'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:employees.hourly-workload'])} label="О графике" trackId="employees.hourly_workload.hint" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <SingleBarChart
              data={hourlyChartData}
              axisFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
              labelFormatter={callsAxisFormatter}
              seriesLabel="Звонки"
            />
          </CardContent>
        </Card>
  );

  const hourlyByDayElement = panels.isVisible(PANEL_IDS['ui:employees.hourly-by-day']) && (
        <Card data-panel-id={PANEL_IDS['ui:employees.hourly-by-day']} className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.hourly-by-day'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:employees.hourly-by-day'])} label="О графике" trackId="employees.hourly_by_day.hint" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <SingleBarChart
              data={durationChartData}
              axisFormatter={minutesAxisFormatter}
              tooltipFormatter={minutesTooltipFormatter}
              labelFormatter={minutesAxisFormatter}
              seriesLabel="Минуты"
            />
          </CardContent>
        </Card>
  );

  const waitHoldHourlyElement = panels.isVisible(PANEL_IDS['ui:employees.wait-hold-hourly']) && (
        <Card data-panel-id={PANEL_IDS['ui:employees.wait-hold-hourly']} className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.wait-hold-hourly'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:employees.wait-hold-hourly'])} label="О графике" trackId="employees.wait_hold_hourly.hint" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <SingleBarChart
              data={waitByHourChartData}
              axisFormatter={minutesAxisFormatter}
              tooltipFormatter={minutesTooltipFormatter}
              labelFormatter={minutesAxisFormatter}
              seriesLabel="Минуты ожидания"
            />
          </CardContent>
        </Card>
  );

  // Тумблером расшифровок карточка не управляется: счёт идёт на сервере в базе и возвращает
  // числа, а не текст разговора.
  const forbiddenPhrasesElement = panels.isVisible(PANEL_IDS['ui:employees.forbidden-phrases']) && (
      <Card data-panel-id={PANEL_IDS['ui:employees.forbidden-phrases']}>
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-base">{panels.title(PANEL_IDS['ui:employees.forbidden-phrases'])}</CardTitle>
            <InfoHint text={panels.description(PANEL_IDS['ui:employees.forbidden-phrases'])} label="О графике" trackId="employees.forbidden_phrases.hint" />
          </div>
        </CardHeader>
        <CardContent>
          <ForbiddenPhraseChart rows={forbiddenPhraseChartData} onOpenCallJournal={openJournal} />
        </CardContent>
      </Card>
  );

  const element = (id, node) => node && { id, element: node };
  const entries = [
    element(PANEL_IDS['ui:employees.header'], headerElement),
    element('ui:employees.kpi-strip', kpiStripElement),
    element(PANEL_IDS['ui:employees.operator-efficiency'], efficiencyElement),
    element(PANEL_IDS['ui:employees.quality-comparison'], qualityComparisonElement),
    element(PANEL_IDS['ui:employees.quality-trend'], qualityTrendElement),
    element(PANEL_IDS['ui:employees.calls-trend'], callsTrendElement),
    element(PANEL_IDS['ui:employees.weekday-workload'], weekdayWorkloadElement),
    element(PANEL_IDS['ui:employees.hourly-workload'], hourlyWorkloadElement),
    element(PANEL_IDS['ui:employees.hourly-by-day'], hourlyByDayElement),
    element(PANEL_IDS['ui:employees.wait-hold-hourly'], waitHoldHourlyElement),
    element(PANEL_IDS['ui:employees.forbidden-phrases'], forbiddenPhrasesElement),
    ...nodeItems.entries
  ].filter(Boolean);

  const defaults = buildTabDefaults('employees', {
    nodeSections: nodeItems.nodeSections,
    hiddenNodeItems: nodeItems.hiddenNodeItems
  });

  return (
    <div className="space-y-6">
      {employeesError && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          {employeesError}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <SectionGrid
        tab="employees"
        entries={entries}
        defaults={defaults}
        hiddenEntries={nodeItems.hiddenEntries}
      />
      {nodeItems.notice}
    </div>
  );
};

const PhoneIcon = (props) => <Activity {...props} />;

/**
 * Заголовок столбца — кнопка: сортировка доступна с клавиатуры, а имя кнопки совпадает с
 * подписью столбца. У активного столбца — стрелка направления, у остальных двойная стрелка
 * появляется при наведении и фокусе: постоянная читалась как относящаяся к соседнему столбцу.
 * Стрелка вне потока: в числовом столбце она не отодвигает подпись от правого края цифр и не
 * тратит ширину таблицы.
 */
const EfficiencySortButton = ({ label, direction, numeric, onClick }) => {
  const Icon = direction === 'asc' ? ChevronUp : direction === 'desc' ? ChevronDown : ChevronsUpDown;

  return (
    <button
      type="button"
      data-ev="employees.efficiency.sort"
      onClick={onClick}
      className={`group relative inline-block rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        numeric ? 'text-right' : 'text-left'
      } ${direction ? 'font-semibold text-foreground' : ''}`}
    >
      {label}
      <Icon
        aria-hidden="true"
        className={`absolute top-1/2 h-3 w-3 -translate-y-1/2 ${numeric ? 'right-full mr-0.5' : 'left-full ml-0.5'} ${direction ? '' : 'opacity-0 group-hover:opacity-40 group-focus-visible:opacity-40'}`}
      />
    </button>
  );
};

// Цвет — не единственный носитель смысла: стрелка рядом и подпись для скринридера.
const HighlightMark = ({ highlight }) => {
  if (!highlight) {
    return null;
  }

  const Icon = highlight === 'best' ? TrendingUp : TrendingDown;

  return (
    <>
      <Icon aria-hidden="true" className="h-3 w-3" />
      <span className="sr-only">{EFFICIENCY_HIGHLIGHT_STYLES[highlight].label}</span>
    </>
  );
};

/**
 * Отметка о малой выборке — значок в постоянном слоте после значения, а словами она названа под
 * таблицей: текст в каждой строке раздувал колонки, и чек-лист с жалобами уезжали за край карточки.
 */
const InsufficientDataSlot = ({ metric, threshold, insufficient }) => {
  const description = describeInsufficientData(metric, threshold);

  return (
    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center" title={insufficient ? description : undefined}>
      {insufficient && (
        <>
          <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="sr-only" title={description}>{INSUFFICIENT_DATA_LABEL}</span>
        </>
      )}
    </span>
  );
};

// Прочерк на месте стрелки у значения без подсветки: плашки колонки читаются одинаково. Если нет
// самого значения (подпись и есть прочерк), второй ставить незачем.
const RankedValue = ({ metric, label, highlight, muted }) => {
  if (label === EMPTY_VALUE) {
    return <TableValue width="md" tone="muted" className="justify-center">{label}</TableValue>;
  }

  return (
    <TableValue
      width="md"
      tone={muted ? 'muted' : 'neutral'}
      className={highlight ? EFFICIENCY_HIGHLIGHT_STYLES[highlight].className : undefined}
      title={describeHighlight(metric, highlight)}
    >
      {highlight ? <HighlightMark highlight={highlight} /> : <Minus aria-hidden="true" className="h-3 w-3" />}
      {label}
    </TableValue>
  );
};

const ComplaintCount = ({ count }) => {
  const { label, tone } = resolveComplaintStyle(count);
  return <TableValue tone={tone}>{label}</TableValue>;
};

const EMPTY_VALUE = '—';

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const formatPercent = (value) => {
  const numeric = readFiniteNumber(value);
  return numeric === null ? EMPTY_VALUE : `${clampPercent(numeric).toFixed(1).replace('.', ',')}%`;
};

/**
 * Конверсия: цвет означает только место среди сотрудников с достаточными данными. Прежняя
 * абсолютная шкала (красный до 20%) убрана: 15% у лучшего салона было бы красным рядом с
 * зелёной подсветкой лучших.
 */
const ConversionCell = ({ value, highlight, insufficient, threshold }) => (
  <div className="flex items-center gap-1">
    <RankedValue metric="conversion" label={formatPercent(value)} highlight={highlight} muted={insufficient} />
    <InsufficientDataSlot metric="conversion" threshold={threshold} insufficient={insufficient} />
  </div>
);

const CHECKLIST_BAR_CLASS = 'h-1.5 w-16 shrink-0 max-sm:w-12';

const ChecklistTotalCell = ({ value, highlight, insufficient, threshold }) => {
  const numeric = readFiniteNumber(value);

  // Оценок нет вовсе — сравнивать нечего, отметка «Недостаточно данных» тут лишняя. Пустая дорожка
  // держит прочерк в той же колонке, что и цифры соседних строк.
  if (numeric === null) {
    return (
      <div className="flex items-center gap-1">
        <div aria-hidden="true" className={`${CHECKLIST_BAR_CLASS} rounded-full bg-primary/10`} />
        <TableValue width="md" tone="muted" className="justify-center">{EMPTY_VALUE}</TableValue>
        <InsufficientDataSlot metric="checklist" threshold={threshold} insufficient={false} />
      </div>
    );
  }

  const clamped = clampPercent(numeric);

  return (
    <div className="flex items-center gap-1">
      <Progress value={clamped} className={CHECKLIST_BAR_CLASS} />
      <RankedValue metric="checklist" label={formatPercent(clamped)} highlight={highlight} muted={insufficient} />
      <InsufficientDataSlot metric="checklist" threshold={threshold} insufficient={insufficient} />
    </div>
  );
};

const mergeEmployeeOptions = (baseEmployees, analyticRows) => {
  const byId = new Map();

  const add = (employee) => {
    const id = employee?.id?.toString?.() ?? employee?.employeeId?.toString?.();
    if (!id) {
      return;
    }

    const existing = byId.get(id);
    byId.set(id, {
      id,
      name: employee?.name ?? employee?.fullName ?? employee?.employeeName ?? existing?.name ?? 'Без имени',
      department: employee?.department ?? existing?.department
    });
  };

  (baseEmployees ?? []).forEach(add);
  (analyticRows ?? []).forEach(add);

  return Array.from(byId.values())
    .sort((left, right) => left.name.localeCompare(right.name, 'ru', { sensitivity: 'base' }));
};

export default EmployeeAnalytics;
